import type { MaterialRow, PriceListData, TagRow, TagSetRow, Wording } from "./meta";
import { type TagData, tagsByItem } from "./productTags";
import { type ChoiceTag, type IncludeTag, type LineTag, type NameParts, type NameTagConfig, nameUpdate, type OwnTags, same, type SetTags, setTagsName, splitName } from "./names";

/*
 * Tags in names (Price List): a tag (itemTags.ts) can put wording in an item's name, in its brackets ("Supply Only"), its includes
 * or excludes ("Includes Keys") or a line under it ("SPECIAL ORDER ITEM (3-7 BUSINESS DAYS)"); a tag set can too, when an item has
 * any of its tags (Keyway: "Includes Keys"). An item's tags are kept on it (item_tags), and its name follows them: the name its
 * tags give it (smartName) is its name with exactly their wording, no other. A name that reads differently (a tag changed, or the
 * name edited in ServiceM8) shows as a name to update, never changed by itself; to keep such a name, change its tags. names.ts
 * reads and writes the wording: its config (NameTagConfig) is worked out from the tags here (deriveConfig), one-only sets as its
 * choices.
 *
 * An item linked to products has their product tags too (productTags.ts), and so their wording: "Installed cylinder" tagged
 * Installation, linked to a cylinder tagged LW4 (Keyway: Includes Keys), is named "... (Includes Installation & Keys)". Two
 * setting one tag differently clash: it's left as the name has it. Shared by the server and the screens; no server or browser imports.
 */

const LIST_WORD = { includes: "Includes", excludes: "Excludes" } as const;

/** A wording as it reads in a name: "Supply Only", "Includes Keys", "SPECIAL ORDER ITEM (3-7 BUSINESS DAYS)". */
export function wordingText(w: Wording) {
    if (w.kind === "brackets") return w.text.trim();
    if (w.kind === "line") return w.text.trim();
    return `${LIST_WORD[w.kind]} ${w.text.trim()}`;
}

/** A tag set's wording's owner in names.ts's config (its tags have their own ids). */
const setOwner = (setId: string) => `${setId}:w`;

/** What comes first: tags not in a set and sets, by their place in the order; a set's tags by theirs. */
export function inOrder(data: Pick<PriceListData, "tags" | "tagSets">): ({ set: TagSetRow; tags: TagRow[] } | { set: null; tags: [TagRow] })[] {
    const sets = data.tagSets.map((set) => ({ set, tags: data.tags.filter((t) => t.setId === set.id).toSorted((a, b) => a.position - b.position) }));
    const loose = data.tags.filter((t) => !t.setId || !data.tagSets.some((s) => s.id === t.setId)).map((t) => ({ set: null, tags: [t] as [TagRow] }));
    return [...sets, ...loose].toSorted((a, b) => (a.set?.position ?? a.tags[0]?.position ?? 0) - (b.set?.position ?? b.tags[0]?.position ?? 0));
}

/** A one-only set's option wording ("Supply Only", "Includes Installation"), or null (a line: a line of its own). */
const optionText = (w: Wording) => (w.kind === "line" ? null : wordingText(w));

/** An earlier wording as it read ("Includes Keys"), as an include's word ("Keys"), or null: it wasn't one. */
const listedWord = (a: string) => /^\s*(?:includes?|excludes?)\s+(.+)$/i.exec(a)?.[1]?.trim() ?? null;

/**
 * names.ts's config for the tags: a one-only set is a choice (its tags its options, "Includes Installation" among them joining the
 * list), a tag in brackets on its own a choice of one; includes and excludes are includes (one per word, whichever tags say it);
 * lines are lines (one per text, as it reads). Earlier wordings (`aliases`, kept as they read in a name: "Includes Keys") are
 * still read; `retired` ones too, and taken out when a name is updated.
 */
export function deriveConfig(data: Pick<PriceListData, "tags" | "tagSets">, retired: NameTagConfig["retired"]): NameTagConfig {
    const choices: ChoiceTag[] = [];
    const includes: IncludeTag[] = [];
    const lines: LineTag[] = [];
    const include = (owner: string, text: string, aliases: string[]) => {
        let found = includes.find((i) => same(i.name, text));
        if (!found) includes.push((found = { id: owner, name: text.trim(), aliases: [] }));
        // An include's earlier words (one that wasn't an include can't be read as one).
        const words = aliases.flatMap((a) => listedWord(a) ?? []);
        found.aliases = [...new Set([...(found.aliases ?? []), ...words])];
    };
    // A line as it reads (lead time and all): each its own (the same line said by two tags, one).
    const line = (owner: string, label: string, text: string, aliases: string[]) => {
        let found = lines.find((l) => same(l.text, text));
        if (!found) lines.push((found = { id: owner, label, text: text.trim(), values: [], aliases: [] }));
        found.aliases = [...new Set([...(found.aliases ?? []), ...aliases])];
    };
    // A wording of its own (not one of a one-only set's options).
    const alone = (owner: string, label: string, w: Wording, aliases: string[]) => {
        if (w.kind === "brackets") choices.push({ id: owner, label, options: [{ id: owner, text: w.text.trim(), aliases }] });
        else if (w.kind === "line") line(owner, label, w.text, aliases);
        else include(owner, w.text, aliases);
    };
    for (const entry of inOrder(data)) {
        const { set } = entry;
        if (set?.oneOnly) {
            const options = entry.tags.flatMap((t) => {
                const text = t.wording ? optionText(t.wording) : null;
                return text ? [{ id: t.id, text, aliases: t.aliases }] : [];
            });
            if (options.length) choices.push({ id: set.id, label: set.name, options });
            for (const t of entry.tags) if (t.wording?.kind === "line") line(t.id, t.name, t.wording.text, t.aliases);
        } else {
            for (const t of entry.tags) if (t.wording) alone(t.id, t.name, t.wording, t.aliases);
        }
        if (set?.wording) alone(setOwner(set.id), set.name, set.wording, set.aliases);
    }
    return { choices, includes, lines, retired };
}

/**
 * Each tag's (and set's) earlier wordings, from a config that has them (names.ts withHistory, after an edit): what's kept as their
 * `aliases`, as they read in a name ("Includes Keys"). By tag or set id.
 */
export function aliasesFrom(data: Pick<PriceListData, "tags" | "tagSets">, cfg: NameTagConfig): Map<string, string[]> {
    const out = new Map<string, string[]>();
    const find = (owner: string, w: Wording, oneOnly: string | null) => {
        if (oneOnly && w.kind !== "line") return cfg.choices.find((c) => c.id === oneOnly)?.options.find((o) => o.id === owner)?.aliases;
        if (w.kind === "brackets") return cfg.choices.find((c) => c.id === owner)?.options[0]?.aliases;
        if (w.kind === "line") return cfg.lines.find((l) => same(l.text, w.text))?.aliases;
        const word = LIST_WORD[w.kind];
        return cfg.includes.find((i) => same(i.name, w.text))?.aliases?.map((a) => `${word} ${a}`);
    };
    for (const t of data.tags) {
        const set = data.tagSets.find((s) => s.id === t.setId);
        if (t.wording) out.set(t.id, find(t.id, t.wording, set?.oneOnly ? set.id : null) ?? []);
    }
    for (const s of data.tagSets) if (s.wording) out.set(s.id, find(setOwner(s.id), s.wording, null) ?? []);
    return out;
}

/** What a wording sets in a name, as names.ts has it (config ids): `owner` the tag's (or set's) id, `oneOnly` its one-only set's. */
function wordingSet(cfg: NameTagConfig, owner: string, w: Wording, oneOnly: string | null): OwnTags {
    if (oneOnly && w.kind !== "line") return { choices: { [oneOnly]: owner } };
    if (w.kind === "brackets") return { choices: { [owner]: owner } };
    if (w.kind === "line") {
        const id = cfg.lines.find((l) => same(l.text, w.text))?.id;
        return id ? { lines: { [id]: "" } } : {};
    }
    const id = cfg.includes.find((i) => same(i.name, w.text))?.id;
    return id ? { includes: { [id]: w.kind } } : {};
}

/** What a tag's wording sets in a name (none: it has none). */
export function tagSet(data: Pick<PriceListData, "tagSets" | "nameTags">, tag: TagRow): OwnTags {
    if (!tag.wording) return {};
    const set = data.tagSets.find((s) => s.id === tag.setId);
    return wordingSet(data.nameTags, tag.id, tag.wording, set?.oneOnly ? set.id : null);
}

/** What a set's wording sets in a name (none: it has none). */
const setSet = (data: Pick<PriceListData, "nameTags">, set: TagSetRow): OwnTags => (set.wording ? wordingSet(data.nameTags, setOwner(set.id), set.wording, null) : {});

/** Whether a name's parts have all a set of tags says. */
function hasAll(parts: NameParts, set: OwnTags) {
    const entries = [
        ...Object.entries(set.choices ?? {}).map(([id, v]) => parts.choices[id] === v),
        ...Object.entries(set.includes ?? {}).map(([id, kind]) => (kind ? parts[kind].includes(id) : !parts.includes.includes(id) && !parts.excludes.includes(id))),
        ...Object.entries(set.lines ?? {}).map(([id, v]) => (v === null ? !(id in parts.lines) : same(parts.lines[id] ?? "\0", v))),
    ];
    return entries.length > 0 && entries.every(Boolean);
}

/** The first one-only set an item has more than one tag of (it can have one at most), or null. */
export function oneOnlyClash(data: TagData, uuid: string): TagSetRow | null {
    const ids = tagsByItem(data)[uuid];
    if (!ids || ids.length < 2) return null;
    return data.tagSets.find((s) => s.oneOnly && data.tags.filter((t) => t.setId === s.id && ids.includes(t.id)).length > 1) ?? null;
}

/** Items with more than one of these tags (`itemTags`: item -> its tags): what would break a one-only set of them. */
export function havingSeveral(itemTags: Record<string, string[]>, tagIds: readonly string[]): string[] {
    const ids = new Set(tagIds);
    return Object.keys(itemTags).filter((uuid) => (itemTags[uuid] ?? []).filter((id) => ids.has(id)).length > 1);
}

/** What working tags out needs (with products', groups' and what items are linked to: their tags too, productTags.ts). */
export type SmartData = Pick<PriceListData, "tags" | "tagSets" | "itemTags" | "materials" | "nameTags"> & Partial<Pick<PriceListData, "itemPricing" | "groups" | "packages">>;

/** The tags whose wording a name has (reading tags out of names, once: itemTags.ts). */
export function tagsInName(data: Pick<SmartData, "tags" | "tagSets" | "nameTags">, name: string): string[] {
    if (!data.tags.some((t) => t.wording)) return [];
    const parts = splitName(name, data.nameTags);
    return data.tags.filter((t) => t.wording && hasAll(parts, tagSet(data, t))).map((t) => t.id);
}

// The tags in order, worked out once per data load (every row asks).
const orderCache = new WeakMap<object, TagRow[]>();

/** An item's tags (ids), in order: its own, and those its products and group give it (productTags.ts). */
export function tagIdsOf(data: TagData, uuid: string): string[] {
    const kept = tagsByItem(data)[uuid];
    if (!kept?.length) return [];
    return orderedTags(data)
        .filter((t) => kept.includes(t.id))
        .map((t) => t.id);
}

/** Every tag, in order (by its place, a set's in the set's). */
export function orderedTags(data: Pick<PriceListData, "tags" | "tagSets">): TagRow[] {
    let order = orderCache.get(data.tags);
    if (!order) orderCache.set(data.tags, (order = inOrder(data).flatMap((e) => e.tags)));
    return order;
}

/** An item's tags, in order. */
export const tagsOf = (data: SmartData, uuid: string): TagRow[] => {
    const ids = new Set(tagIdsOf(data, uuid));
    return orderedTags(data).filter((t) => ids.has(t.id));
};

export interface SmartTags {
    /** What gives it wording: its tags and their sets (Keyway), by name. */
    from: string[];
    /** The wording they set (names.ts SetTags: config ids; those clashing left out). */
    set: SetTags;
    /** Wordings (tagKey) two of them set differently: left as its name has them. */
    clashes: string[];
}

/** The wording an item's name should have: its tags' and their sets'. */
export function smartTagsOf(data: SmartData, uuid: string): SmartTags {
    const setOf = new Map(data.tags.map((t) => [t.id, data.tagSets.find((s) => s.id === t.setId)]));
    const sources: { name: string; set: OwnTags }[] = [];
    for (const id of tagIdsOf(data, uuid)) {
        const tag = data.tags.find((t) => t.id === id);
        if (!tag) continue;
        if (tag.wording) sources.push({ name: tag.name, set: tagSet(data, tag) });
        const set = setOf.get(id);
        if (set?.wording && !sources.some((s) => s.name === set.name)) sources.push({ name: set.name, set: setSet(data, set) });
    }
    // Merged; two saying one tag differently clash.
    const values = new Map<string, string | null>();
    const clashes = new Set<string>();
    const from: string[] = [];
    for (const s of sources) {
        let gave = false;
        for (const [field, kind] of [["choices", "choice"], ["includes", "include"], ["lines", "line"]] as const) {
            for (const [id, v] of Object.entries(s.set[field] ?? {})) {
                const key = `${kind}:${id}`;
                gave = true;
                if (!values.has(key)) values.set(key, v);
                else if (values.get(key) !== v) clashes.add(key);
            }
        }
        if (gave && !from.includes(s.name)) from.push(s.name);
    }
    const set: SetTags = { choices: {}, includes: {}, lines: {} };
    for (const [key, v] of values) {
        if (clashes.has(key)) continue;
        const at = key.indexOf(":");
        const kind = key.slice(0, at) as "choice" | "include" | "line";
        const id = key.slice(at + 1);
        if (kind === "choice") set.choices[id] = v;
        else if (kind === "line") set.lines[id] = v;
        else set.includes[id] = v as SetTags["includes"][string];
    }
    return { from, set, clashes: [...clashes] };
}

/** Every wording names.ts knows, as its tags say it (the rest: none), but those that clash: left as the name has them. */
function wholeSet(cfg: NameTagConfig, smart: SmartTags): SetTags {
    const clash = new Set(smart.clashes);
    const each = <T>(kind: string, ids: string[], given: Record<string, T>) =>
        Object.fromEntries(ids.filter((id) => !clash.has(`${kind}:${id}`)).map((id) => [id, id in given ? given[id] : null])) as Record<string, T | null>;
    return {
        choices: each("choice", cfg.choices.map((c) => c.id), smart.set.choices),
        includes: each("include", cfg.includes.map((i) => i.id), smart.set.includes) as SetTags["includes"],
        lines: each("line", cfg.lines.map((l) => l.id), smart.set.lines),
    };
}

// Each item's name from its tags, worked out once per data load (every row asks).
const nameCache = new WeakMap<object, Map<string, string | null>>();

/**
 * The name an item's tags give it: its name with their wording, no other (old wording tidied away), if that reads differently
 * from its name now; else null.
 */
export function smartName(data: SmartData, item: Pick<MaterialRow, "uuid" | "name">): string | null {
    let byItem = nameCache.get(data);
    if (!byItem) nameCache.set(data, (byItem = new Map()));
    const key = `${item.uuid}\u0000${item.name}`;
    if (byItem.has(key)) return byItem.get(key) ?? null;
    const tidy = nameUpdate(item.name, data.nameTags) ?? item.name;
    const next = setTagsName(tidy, wholeSet(data.nameTags, smartTagsOf(data, item.uuid)), data.nameTags) ?? tidy;
    const out = next === item.name ? null : next;
    byItem.set(key, out);
    return out;
}

